(() => {
  const SELECTORS = {
    component: "ff-faqs",
    faqItem: ".ff-faqs__faq",
    question: ".ff-faqs__question",
    answer: ".ff-faqs__answer",
    icon: "svg, .icon",
    schemaScript: 'script[type="application/ld+json"][data-faq-schema]',
  };

  const ATTRIBUTES = {
    ariaExpanded: "aria-expanded",
    dataFaqSchema: "data-faq-schema",
  };

  const CLASSES = {
    active: "active",
  };

  const SCRIPT_TYPES = {
    jsonLd: "application/ld+json",
  };

  const isAlreadyDefined = Boolean(customElements.get(SELECTORS.component));

  class FiftyFlowersFAQs extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    connectedCallback() {
      this.addEventListener("click", this.handleClick);
      this.addEventListener("keydown", this.handleKeyDown);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
      this.removeEventListener("keydown", this.handleKeyDown);
    }

    toggleFAQ(faq) {
      const isActive = faq.classList.contains(CLASSES.active);
      faq.classList.toggle(CLASSES.active);
      faq.setAttribute(ATTRIBUTES.ariaExpanded, !isActive);
    }

    handleClick(event) {
      const faq = event.target.closest(SELECTORS.faqItem);
      if (!faq) return;

      const answer = faq.querySelector(SELECTORS.answer);
      const interactive =
        answer &&
        answer.contains(event.target) &&
        event.target.closest("a, button, input, textarea, select, summary");

      if (!interactive) {
        this.toggleFAQ(faq);
      }
    }

    handleKeyDown(event) {
      const faq = event.target.closest(SELECTORS.faqItem);
      if (!faq) return;

      const answer = faq.querySelector(SELECTORS.answer);
      const interactive =
        answer &&
        answer.contains(event.target) &&
        event.target.closest("a, button, input, textarea, select, summary");
      const isSelfFocused =
        event.target === faq || event.target.closest(SELECTORS.question);

      if (
        !interactive &&
        isSelfFocused &&
        (event.key === "Enter" || event.key === " ")
      ) {
        event.preventDefault();
        this.toggleFAQ(faq);
      }
    }
  }

  if (!isAlreadyDefined) {
    customElements.define(SELECTORS.component, FiftyFlowersFAQs);
  }

  /**
   * Extract FAQ data from DOM elements
   * Reads questions and answers from all ff-faqs components on the page
   */
  function extractFAQsFromDOM() {
    const faqComponents = document.querySelectorAll(SELECTORS.component);
    const faqs = [];

    faqComponents.forEach((component) => {
      const faqItems = component.querySelectorAll(SELECTORS.faqItem);

      faqItems.forEach((faqItem) => {
        const questionEl = faqItem.querySelector(SELECTORS.question);
        const answerEl = faqItem.querySelector(SELECTORS.answer);

        if (questionEl && answerEl) {
          const questionText = questionEl.cloneNode(true);
          const icon = questionText.querySelector(SELECTORS.icon);
          if (icon) {
            icon.remove();
          }

          const question = questionText.textContent.trim();
          const answer = answerEl.textContent.trim();

          if (question && answer) {
            faqs.push({ question, answer });
          }
        }
      });
    });

    return faqs;
  }

  function generateFAQSchema() {
    if (document.querySelector(SELECTORS.schemaScript)) {
      return;
    }

    const faqs = extractFAQsFromDOM();

    if (faqs.length === 0) {
      return;
    }

    const mainEntity = faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    }));

    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: mainEntity,
    };

    const script = document.createElement("script");
    script.type = SCRIPT_TYPES.jsonLd;
    script.setAttribute(ATTRIBUTES.dataFaqSchema, "true");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  }

  /**
   * Set up MutationObserver to watch for FAQ components being added to the DOM
   * This ensures schema is generated even if components render asynchronously
   */
  function setupSchemaObserver() {
    let debounceTimer = null;

    const debouncedGenerateSchema = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        generateFAQSchema();
        if (document.querySelector(SELECTORS.schemaScript)) {
          observer.disconnect();
        }
      }, 100);
    };

    const observer = new MutationObserver((mutations) => {
      const hasFAQComponents = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return false;
          return (
            node.matches?.(SELECTORS.component) ||
            node.querySelector?.(SELECTORS.component)
          );
        });
      });

      if (hasFAQComponents) {
        debouncedGenerateSchema();
      }
    });

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        generateFAQSchema();
        if (!document.querySelector(SELECTORS.schemaScript)) {
          observer.observe(document.body, {
            childList: true,
            subtree: true,
          });
        }
      });
    } else {
      generateFAQSchema();
      if (!document.querySelector(SELECTORS.schemaScript)) {
        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }
    }
  }

  setupSchemaObserver();
})();
